import axios from 'axios';
import { API_URL } from '../config';

// Create axios instance for general API calls (uses main auth token)
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Create axios instance for user-specific API calls (uses user auth token)
const userApiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Helper function to get main auth token
const getMainToken = () => {
  return localStorage.getItem('auth_token');
};

// Helper function to get user auth token
const getUserToken = () => {
  return localStorage.getItem('user_auth_token');
};

// Helper function to logout
const handleLogout = () => {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('user_auth_token');
  localStorage.removeItem('user_data');
  localStorage.removeItem('company_id');
  window.location.href = '/';
};

// Add request interceptor to general apiClient (uses main auth token)
apiClient.interceptors.request.use(
  (config) => {
    const token = getMainToken();
    if (token) {
      config.headers.Authorization = `Basic ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add request interceptor to userApiClient (uses user auth token)
userApiClient.interceptors.request.use(
  (config) => {
    const token = getUserToken();
    if (token) {
      config.headers.Authorization = `Basic ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors for general apiClient
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - clear auth and redirect to login
      //handleLogout();
    }
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors for userApiClient
userApiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - clear auth and redirect to login
      handleLogout();
    }
    return Promise.reject(error);
  }
);

export default apiClient;
export { userApiClient };


export async function getSiteVisitForms(companyId, userId = null) {
  if (!companyId) throw new Error('companyId required');
  
  // If userId is provided, use user-specific endpoint
  let url;
  if (userId) {
    url = `${API_URL}/companies/${companyId}/site_visit_forms/users/${userId}`;
  } else {
    url = `${API_URL}/companies/${companyId}/site_visit_forms/list`;
  }
  
  const resp = await apiClient.get(url);
  return resp.data;
}

export async function getSiteVisitFormsById(companyId, formId) {
  if (!companyId) throw new Error('companyId required');
  if (!formId) throw new Error('formId required');
  const url = `${API_URL}/companies/${companyId}/site_visit_forms/${formId}`;
  const resp = await apiClient.get(url);
  
  // Extract form data
  const formData = resp.data.data;
  
  // Check if meta contains admin_email and token for generating new auth token
  if (formData.meta && formData.meta.admin_email && formData.meta.token) {
    const adminEmail = formData.meta.admin_email;
    const adminToken = formData.meta.token;
    
    console.log('Generating new auth token with admin credentials:', { adminEmail, adminToken });
    if(adminEmail && adminToken){
  // Generate admin token and update stored credentials (avoid circular dependency)
    const { default: authService } = await import('./authService');
    authService.generateAdminToken(adminEmail, adminToken, companyId);
    
    console.log('Updated auth token for admin access');
    }
    
  }
  
  return formData;
}


export async function getLeadByPhoneNumber(companyId,phone) {
  if (!companyId) throw new Error('companyId required');
  if (!phone) throw new Error('phone required');
  const url = `${API_URL}/companies/${companyId}/leads/search?q=${phone}`;
  const resp = await apiClient.get(url);
  return resp;
}

export async function getLeadById(companyId, leadId) {
  if (!companyId) throw new Error('companyId required');
  if (!leadId) throw new Error('leadId required');
  const url = `${API_URL}/companies/${companyId}/leads/${leadId}`;
  const resp = await apiClient.get(url);
  return resp.data;
}

// Helper function to clean payload by removing undefined, null, empty strings, and empty arrays
const cleanPayload = (data) => {
  if (data === null || data === undefined) return null;
  
  if (Array.isArray(data)) {
    const cleanedArray = data
      .map(item => cleanPayload(item))
      .filter(item => item !== null && item !== undefined);
    return cleanedArray.length > 0 ? cleanedArray : null;
  }
  
  if (typeof data === 'object') {
    const cleanedObj = {};
    for (const [key, value] of Object.entries(data)) {
      const cleanedValue = cleanPayload(value);
      if (cleanedValue !== null && cleanedValue !== undefined && cleanedValue !== '') {
        cleanedObj[key] = cleanedValue;
      }
    }
    return Object.keys(cleanedObj).length > 0 ? cleanedObj : null;
  }
  
  // For primitive values, return null if empty string, otherwise return the value
  return data === '' ? null : data;
};

export async function upsertLead(companyId, leadData) {
  if (!companyId) throw new Error('companyId required');
  if (!leadData) throw new Error('leadData required');
  
  const url = `${API_URL}/companies/${companyId}/leads/upsert`;
  
  // Clean payload to remove undefined, null, empty values
  const cleanedData = cleanPayload(leadData);
  
  // Filter out any remaining empty values at the top level
  const filteredData = Object.fromEntries(
    Object.entries(cleanedData || {}).filter(([_, value]) => {
      if (value === null || value === undefined || value === '') return false;
      if (Array.isArray(value) && value.length === 0) return false;
      return true;
    })
  );
  
  console.log("API Service - Upsert", filteredData);
  
  const resp = await apiClient.post(url, filteredData);
  return resp.data;
}

export async function modifyLead(companyId, leadId, leadData) {
  if (!companyId) throw new Error('companyId required');
  if (!leadId) throw new Error('leadId required');
  if (!leadData) throw new Error('leadData required');
  
  const url = `${API_URL}/companies/${companyId}/leads/upsert`;
  
  // Clean payload to remove undefined, null, empty values
  const cleanedData = cleanPayload(leadData);
  
  // Filter out any remaining empty values at the top level
  const filteredData = Object.fromEntries(
    Object.entries(cleanedData || {}).filter(([_, value]) => {
      if (value === null || value === undefined || value === '') return false;
      if (Array.isArray(value) && value.length === 0) return false;
      return true;
    })
  );
  
  console.log("API Service - Modify", { leadId, filteredData });
  
  const resp = await apiClient.post(url, filteredData);
  return resp.data;
}

export async function assignLead(companyId, leadId, toUserEmail) {
  if (!companyId) throw new Error('companyId required');
  if (!leadId) throw new Error('leadId required');
  if (!toUserEmail) throw new Error('toUserEmail required');
  
  const url = `${API_URL}/companies/${companyId}/leads/${leadId}/assign`;
  
  const resp = await apiClient.post(url, {

      to_user_email: toUserEmail

  });
  
  return resp.data;
}

export async function getKernelParams(companyId) {
  if (!companyId) throw new Error('companyId required');
  const url = `${API_URL}/companies/${companyId}/leads/kernel_params_json`;
  const resp = await apiClient.get(url);
  return resp.data;
}


export async function getCompanySettings(companyId) {
  if (!companyId) throw new Error('companyId required');
  const url = `${API_URL}/companies/${companyId}/settings`;
  const resp = await apiClient.get(url);
  return resp.data;
}

// Cache for kernel params to avoid repeated API calls
let kernelParamsCache = null;
let kernelParamsCacheCompanyId = null;

export async function getFieldChoices(companyId, fieldName) {
  if (!companyId) throw new Error('companyId required');
  if (!fieldName) throw new Error('fieldName required');

  // Fetch kernel params if not cached or company changed
  if (!kernelParamsCache || kernelParamsCacheCompanyId !== companyId) {
    const data = await getKernelParams(companyId);
    kernelParamsCache = data;
    kernelParamsCacheCompanyId = companyId;
  }

  // Find the field by name
  const field = kernelParamsCache.lead_fields?.find(
    f => f.name === fieldName || f.label === fieldName
  );

  if (!field) {
    console.warn(`Field "${fieldName}" not found in kernel params`);
    return [];
  }

  // Return choices array
  return field.choices || [];
}

export async function getFieldChoicesByMultipleNames(companyId, fieldNames) {
  if (!companyId) throw new Error('companyId required');
  if (!fieldNames || !Array.isArray(fieldNames)) {
    throw new Error('fieldNames must be an array');
  }

  // Fetch kernel params if not cached or company changed
  if (!kernelParamsCache || kernelParamsCacheCompanyId !== companyId) {
    const data = await getKernelParams(companyId);
    kernelParamsCache = data;
    kernelParamsCacheCompanyId = companyId;
  }

  const result = {};

  fieldNames.forEach(fieldName => {
    const field = kernelParamsCache.lead_fields?.find(
      f => f.name === fieldName || f.label === fieldName
    );

    if (field) {
      result[fieldName] = field.choices || [];
    } else {
      console.warn(`Field "${fieldName}" not found in kernel params`);
      result[fieldName] = [];
    }
  });

  return result;
}

// Clear cache function (useful when data needs to be refreshed)
export function clearKernelParamsCache() {
  kernelParamsCache = null;
  kernelParamsCacheCompanyId = null;
}
